import React from 'react';
import styled from 'styled-components';
import { Link } from 'components/Link';
import { Label, Text } from 'components/Typography';
import { lineHeight, theme, toRem } from 'design';
import { IPaymentSet } from 'types';
import { centsToDollarsDisplay, formatDateForUILongFormat } from 'utils';

interface IPaymentSetListItemProps {
  payment: IPaymentSet;
  dataCy?: string;
}

const Layout = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.xs)};
  border-bottom: ${toRem(1)} solid ${theme.color.neutral[200]};
  padding-bottom: ${toRem(theme.space.lg)};

  p.date {
    color: ${theme.color.neutral[500]};
  }

  a {
    color: ${theme.color.primary[500]};
    text-decoration: none;
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    font-weight: ${theme.font.weight.medium};
    margin-top: ${toRem(theme.space.xs)};
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: ${toRem(0)};
  }
`;

const Wrapper = styled.div<{ $align?: 'left' | 'right' }>`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space['2xs'])};
  align-items: ${({ $align }) => ($align === 'right' ? 'flex-end' : 'flex-start')};

  label {
    color: ${theme.color.neutral[500]};
  }
`;

const PaymentSetListItem = ({ payment, dataCy = 'payment-set-list-item' }: IPaymentSetListItemProps) => (
  <Layout data-cy={dataCy}>
    <Wrapper data-cy={`${dataCy}-payment-info`}>
      <Text size="small" fontWeight="medium" data-cy={`${dataCy}-payment-id`}>
        {payment.id}
      </Text>
      {payment.description && (
        <Text size="xsmall" data-cy={`${dataCy}-payment-description`}>
          {payment.description}
        </Text>
      )}
      <Text size="xsmall" className="date" data-cy={`${dataCy}-payment-created-at`}>
        {formatDateForUILongFormat(payment.createdAt, true)}
      </Text>
      <Link to={`/payments/payment-set/${payment.id}`}>View the payment set</Link>
    </Wrapper>
    <Wrapper data-cy={`${dataCy}-payment-totals`} $align="right">
      <Label data-cy={`${dataCy}-payment-total-label`}>Total</Label>
      <Text size="small" fontWeight="semibold" data-cy={`${dataCy}-payment-total-value`}>
        {centsToDollarsDisplay(payment.totals.net)}
      </Text>
      <Text size="xsmall" className="date" data-cy={`${dataCy}-payees-total`}>
        {payment.totals.payeeCount} Payee{payment.totals.payeeCount === 1 ? '' : 's'}
      </Text>
    </Wrapper>
  </Layout>
);

export default PaymentSetListItem;
